import { useState } from 'react';
import { useMutation } from 'react-apollo';
import omit from 'lodash.omit';
import pMap from 'p-map-browser';

import usePhotosUpload from '../../../../hooks/usePhotosUpload';
import { getErrorString } from '../../../../util/normalize-errors';
import {
  rosterIdentityUpdate as rosterIdentityUpdateMutation,
  rosterIdentityCreateFromIdentity as rosterIdentityCreateFromIdentityMutation,
  rosterIdentityLinkedIdentityCreate as rosterIdentityLinkedIdentityCreateMutation,
  rosterIdentityLinkedIdentityAddressCreate as rosterIdentityLinkedIdentityAddressCreateMutation,
  rosterIdentityLinkedIdentityAddressUpdate as rosterIdentityLinkedIdentityAddressUpdateMutation,
  selectedIdentityUpdate as selectedIdentityUpdateMutation,
  rosterIdentitiesCreate as rosterIdentitiesCreateMutation,
} from '../../../../graphql/mutations/roster-identity';
import {
  identityAlbumFolderTagsCreate as identityAlbumFolderTagsCreateMutation,
  identityAlbumFolderTagsDelete as identityAlbumFolderTagsDeleteMutation,
} from '../../../../graphql/mutations/album-folder-tags';
import { getIdentityAlbumFolderTagsQuery } from '../../../temp/views/onboarding/TagSelection/graphql/queries/getIdentityAlbumFolderTags';
import { AlbumFolderTags } from '../../../../graphql/queries/album-folder-tags';
import { getRosterIdentityLink } from '../../../../util/identity';
import { hierarchyOwnerPurchasedIdentityCreate as hierarchyOwnerPurchasedIdentityCreateMutation } from '../../../../graphql/mutations/hierarchy-items';

const useRosterIdentityUpdate = ({
  onPhotoUploadSuccess,
  currentClaimedIdentity,
} = {}) => {
  const [rosterIdentityUpdate] = useMutation(rosterIdentityUpdateMutation);
  const [selectedIdentityUpdate] = useMutation(selectedIdentityUpdateMutation);
  const [rosterIdentitiesCreate] = useMutation(rosterIdentitiesCreateMutation);
  const [rosterIdentityCreateFromIdentity] = useMutation(
    rosterIdentityCreateFromIdentityMutation,
  );
  const [rosterIdentityLinkedIdentityCreate] = useMutation(
    rosterIdentityLinkedIdentityCreateMutation,
  );
  const [rosterIdentityLinkedIdentityAddressCreate] = useMutation(
    rosterIdentityLinkedIdentityAddressCreateMutation,
  );
  const [rosterIdentityLinkedIdentityAddressUpdate] = useMutation(
    rosterIdentityLinkedIdentityAddressUpdateMutation,
  );

  const [tagsCreate] = useMutation(identityAlbumFolderTagsCreateMutation);
  const [tagsDelete] = useMutation(identityAlbumFolderTagsDeleteMutation);

  const [hierarchyOwnerPurchasedIdentityCreate] = useMutation(
    hierarchyOwnerPurchasedIdentityCreateMutation,
  );

  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const { onPhotoUpload } = usePhotosUpload({
    onPhotoUploadSuccess,
  });
  /**
   * @typedef {Object} LinkedIdentity
   * @property {String} phone_number Linked Identity Phone number.
   * @property {Array<Object>} addresses A list of RosterIdentityAddresses.
   */

  /**
   * Updates a Roster Identity.
   *
   * @param {Object} input The options object.
   * @param {String} input.first_name Roster ID First Name.
   * @param {String} input.last_name Roster ID Last Name.
   * @param {File} input.selfie An image file to replace the current selfie.
   * @param {String} input.id The roster Identity Id.
   * @param {String} input.account_id The Account ID.
   * @param {String} [input.identity_id] The Identity ID. If passed down, the mutation createRosterIdentityFromIdentity will be used.
   * @param {Array<LinkedIdentity>} [input.linked_identities] Linked identities (apply only on create).
   * @param {Boolean} input.photo_share_blocked Roster ID NPTS value.
   */
  const onRosterIdentityUpdate = async input => {
    setUpdating(true);
    setUpdateSuccess(false);
    setUpdateError('');

    try {
      let selfieId;

      const {
        selfie,
        account_id: accountId,
        identity_id: identityId,
        linked_album_ids: linkedAlbumIds = [],
        linked_album_folder_ids: linkedAlbumFolderIds = [],
        owner_purchased_album_ids: ownerPurchasedAlbumIds = [],
        claimed_identity: claimed,
        selected_identity_id: selectedIdentityId,
        email_address: emailAddress,
        create_album_folder_tags: createTags = [],
        ...rosterIdentityMutationInput
      } = input;

      const {
        grade: linkGrade,
        teacher_name: linkTeacherName,
        class_name: linkClassaName,
        organization_ref_id: linkRefId,
      } = rosterIdentityMutationInput;

      const hasLinkData =
        !!linkGrade || !!linkTeacherName || !!linkClassaName || !!linkRefId;

      if (selfie) {
        const [photoUploadData] = await onPhotoUpload({
          files: [selfie],
          accountId,
          selfie_verify: true,
        });

        if (photoUploadData.error) {
          throw photoUploadData.error;
        }

        selfieId = photoUploadData.photoId;
      }

      // Update:
      if (input.roster_identity_id) {
        // Check if there are linked identities to update.
        // Do note that this is performed first so that the update
        // mutation for the roster ID returns a complete-updated object when
        // run (Plus rosterIdentityUpdate doesnt support linked identities at the time of writing)
        const {
          linked_identities: linkedIdentities,
          ...rosterIdentityUpdateInput
        } = rosterIdentityMutationInput;

        const [linkedIdentity] = linkedIdentities ?? [];

        if ((linkedIdentity?.addresses?.length ?? 0) > 0) {
          // At the time, we only support 1 linked  identity to be created/updated.

          if (linkedIdentity.id) {
            // Update/Create the address of the linked Identity.
            const [address] = linkedIdentity.addresses ?? [];

            if (address?.id) {
              // Update this address
              await rosterIdentityLinkedIdentityAddressUpdate({
                variables: {
                  input: {
                    country_id: address.country?.id,
                    roster_identity_linked_identity_address_id: address.id,
                    ...omit(address, ['id', 'country', '__typename']),
                  },
                },
              });
            } else if (address) {
              // Create new address:
              await rosterIdentityLinkedIdentityAddressCreate({
                variables: {
                  input: {
                    roster_identity_linked_identity_id: linkedIdentity.id,
                    ...address,
                  },
                },
              });
            }
          } else {
            // Create the linked identity as a whole new thing.
            await rosterIdentityLinkedIdentityCreate({
              variables: {
                input: {
                  roster_identity_id: input.roster_identity_id,
                  linked_identity: omit(linkedIdentity, ['id']),
                },
              },
            });
          }
        }

        const hasLinkedAlbumFolderData =
          hasLinkData &&
          (linkedAlbumFolderIds.length > 0 || linkedAlbumIds.length > 0);

        const {
          data: {
            rosterIdentityUpdate: { roster_identity: rosterIdentity },
          },
        } = await rosterIdentityUpdate({
          variables: {
            input: {
              ...(hasLinkedAlbumFolderData
                ? omit(rosterIdentityUpdateInput, [
                    'grade',
                    'teacher_name',
                    'class_name',
                    'organization_ref_id',
                  ])
                : rosterIdentityUpdateInput),
              selfie_photo_id: selfieId,
              create_email_addresses: [emailAddress].filter(Boolean),
            },
          },
        });

        if (hasLinkedAlbumFolderData) {
          await rosterIdentityUpdate({
            variables: {
              input: {
                roster_identity_id: rosterIdentity.id,
                update_links: [...linkedAlbumFolderIds, linkedAlbumIds]
                  .map(hierarchyItemId => {
                    const existingLink = getRosterIdentityLink({
                      rosterIdentity,
                      albumId: hierarchyItemId,
                      albumFolderId: hierarchyItemId,
                    });
                    if (!existingLink) {
                      return undefined;
                    }

                    return {
                      roster_identity_link_id: existingLink.id,
                      grade: linkGrade,
                      teacher_name: linkTeacherName,
                      class_name: linkClassaName,
                      ref_id: linkRefId,
                    };
                  })
                  .filter(Boolean),
              },
            },
          });
        }

        if (claimed !== currentClaimedIdentity) {
          await selectedIdentityUpdate({
            variables: {
              input: {
                id: selectedIdentityId,
                claimed,
              },
            },
          });
        }

        if (ownerPurchasedAlbumIds?.length > 0) {
          const identitiesForPurchase = [
            identityId,
            ...(rosterIdentity?.linked_identities?.records?.map(
              ({ identity }) => identity?.id,
            ) ?? []),
          ].filter(Boolean);

          if (identitiesForPurchase.length > 0) {
            await pMap(
              identitiesForPurchase,
              async linkedIdentityIdentityId => {
                await pMap(ownerPurchasedAlbumIds, async albumId =>
                  hierarchyOwnerPurchasedIdentityCreate({
                    variables: {
                      input: {
                        identity_id: linkedIdentityIdentityId,
                        hierarchy_item_id: albumId,
                      },
                    },
                  }),
                );
              },
            );
          }
        }

        setUpdateSuccess(true);
        return rosterIdentity;
      }

      // Create using identity as reference.
      if (identityId) {
        const {
          linked_identities: linkedIdentities = [],
          ...rosterIdentityCreateFromIdentityInput
        } = rosterIdentityMutationInput;

        const [mainLinkedAddentity] = linkedIdentities;

        const {
          data: {
            rosterIdentityCreateFromIdentity: {
              roster_identity: rosterIdentity,
            },
          },
        } = await rosterIdentityCreateFromIdentity({
          variables: {
            input: {
              ...rosterIdentityCreateFromIdentityInput,
              identity_id: identityId,
              linked_album_ids: linkedAlbumIds,
              linked_album_folder_ids: linkedAlbumFolderIds,
              links: [
                ...(linkedAlbumFolderIds ?? []),
                ...(linkedAlbumIds ?? []),
              ].map(hierarchyItemId => ({
                hierarchy_item_id: hierarchyItemId,
                grade: linkGrade,
                teacher_name: linkTeacherName,
                class_name: linkClassaName,
                ref_id: linkRefId,
              })),
              addresses: mainLinkedAddentity?.addresses ?? [],
            },
          },
        });

        if (selfieId) {
          await rosterIdentityUpdate({
            variables: {
              input: {
                roster_identity_id: rosterIdentity.id,
                selfie_photo_id: selfieId,
              },
            },
          });
        }

        if (ownerPurchasedAlbumIds?.length > 0) {
          const identitiesForPurchase = [
            identityId,
            ...(rosterIdentity?.linked_identities?.records?.map(
              ({ identity }) => identity?.id,
            ) ?? []),
          ].filter(Boolean);

          if (identitiesForPurchase.length > 0) {
            await pMap(
              identitiesForPurchase,
              async linkedIdentityIdentityId => {
                await pMap(ownerPurchasedAlbumIds, async albumId =>
                  hierarchyOwnerPurchasedIdentityCreate({
                    variables: {
                      input: {
                        identity_id: linkedIdentityIdentityId,
                        hierarchy_item_id: albumId,
                      },
                    },
                  }),
                );
              },
            );
          }
        }

        setUpdateSuccess(true);
        return rosterIdentity;
      }

      const validLinkedIdentities = (
        rosterIdentityMutationInput?.linked_identities ?? []
      ).filter(linkedIdentity => !!linkedIdentity.phone_number);

      // Create using account as reference
      const {
        data: {
          rosterIdentitiesCreate: {
            roster_identities: createdRosterIdentities = [],
            success: rosterIdentitiesCreateSuccess,
            ...invalidData
          },
        },
      } = await rosterIdentitiesCreate({
        variables: {
          input: {
            skip_errors: false,
            roster_identities: [
              {
                ...rosterIdentityMutationInput,
                linked_identities: validLinkedIdentities,
                email_addresses: emailAddress
                  ? [{ email_address: emailAddress }]
                  : [],
                linked_album_ids: linkedAlbumIds,
                linked_album_folder_ids: linkedAlbumFolderIds,
                links: [
                  ...(linkedAlbumFolderIds ?? []),
                  ...(linkedAlbumIds ?? []),
                ].map(hierarchyItemId => ({
                  hierarchy_item_id: hierarchyItemId,
                  grade: linkGrade,
                  teacher_name: linkTeacherName,
                  class_name: linkClassaName,
                  ref_id: linkRefId,
                })),
                tags: (createTags ?? []).map(tag => ({
                  album_folder_tag_id: tag.album_folder_tag.id,
                  jersey_number: tag.jersey_number,
                  uniform_album_folder_tag_level_id:
                    tag.uniform_album_folder_tag_level?.id,
                  uniform_album_folder_tag_level_team_id:
                    tag.uniform_album_folder_tag_level_team?.id,
                })),
              },
            ],
            // Email-only should always be claimed. Otherwise respect settings
            claimed:
              (!!emailAddress && !validLinkedIdentities?.length) || !!claimed,
            hierarchy_owner_purchase: {
              album_ids: ownerPurchasedAlbumIds,
            },
          },
        },
      });

      if (!rosterIdentitiesCreateSuccess) {
        const invalidValue =
          invalidData?.invalid_phone_numbers?.[0] ??
          invalidData?.invalid_ref_ids?.[0] ??
          invalidData?.invalid_names?.[0] ??
          invalidData?.invalid_addresses?.[0];

        throw new Error(
          `Failed to create member - invalid input: ${invalidValue}`,
        );
      }

      if (selfieId) {
        await rosterIdentityUpdate({
          variables: {
            input: {
              roster_identity_id: createdRosterIdentities[0].id,
              selfie_photo_id: selfieId,
            },
          },
        });
      }

      setUpdateSuccess(true);
      return createdRosterIdentities[0];
    } catch (error) {
      setUpdateError(getErrorString(error));
      return null;
    } finally {
      setUpdating(false);
    }
  };

  const onTagsCreate = async (input, { albumFolderId, identityId }) => {
    try {
      await tagsCreate({
        variables: {
          input: {
            identity_id: identityId,
            album_folder_tags: input.album_folder_tags,
          },
        },
        refetchQueries: [
          {
            query: getIdentityAlbumFolderTagsQuery,
            variables: {
              identityId,
              albumFolderId,
            },
          },
          {
            query: AlbumFolderTags,
            variables: {
              id: albumFolderId,
            },
          },
        ],
        awaitRefetchQueries: true,
      });
    } catch (error) {
      throw new Error(getErrorString(error));
    }
  };

  const onTagsDelete = async (tagIds, { albumFolderId, identityId }) => {
    try {
      await tagsDelete({
        variables: {
          input: {
            ids: tagIds,
          },
        },
        refetchQueries: [
          {
            query: getIdentityAlbumFolderTagsQuery,
            variables: {
              identityId,
              albumFolderId,
            },
          },
          {
            query: AlbumFolderTags,
            variables: {
              id: albumFolderId,
            },
          },
        ],
        awaitRefetchQueries: true,
      });
    } catch (error) {
      throw new Error(getErrorString(error));
    }
  };

  return {
    updating,
    updateError,
    updateSuccess,
    onRosterIdentityUpdate,
    onTagsCreate,
    onTagsDelete,
  };
};

export default useRosterIdentityUpdate;
